import {
  del,
  get,
  patch,
  post,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  ProductVariant,
  ProductVariantMaster,
  ProductVariantMasterPayload,
} from "@project/types/gruh-udhyog";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  fetchListPage,
  unwrapItem,
} from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/product-variant-masters`;
const SILENT: RequestConfig = { showNotification: false };

class GruhUdhyogVariantService {
  listPage = (
    query: ListQuery = {}
  ): Promise<PaginatedResult<ProductVariantMaster>> =>
    fetchListPage<ProductVariantMaster>(`${BASE}/`, query);

  get = async (id: number): Promise<ProductVariantMaster | undefined> => {
    const data = await get<
      ApiItemResponse<ProductVariantMaster> | ProductVariantMaster
    >(`${BASE}/${id}/`, SILENT_LIST);
    return unwrapItem(data);
  };

  create = async (
    payload: ProductVariantMasterPayload
  ): Promise<ProductVariantMaster> => {
    const data = await post<
      ApiItemResponse<ProductVariantMaster> | ProductVariantMaster,
      ProductVariantMasterPayload
    >(`${BASE}/`, payload, SILENT);
    const item = unwrapItem(data);
    if (!item) {
      throw new Error("Failed to create product variant master.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: Partial<ProductVariantMasterPayload>
  ): Promise<ProductVariantMaster | undefined> => {
    const data = await patch<
      ApiItemResponse<ProductVariantMaster> | ProductVariantMaster,
      Partial<ProductVariantMasterPayload>
    >(`${BASE}/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  delete = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };

  /** Lookup for product-variant SKUs (stock entries, etc.). */
  listLookup = async (search = "") => {
    const result = await fetchListPage<ProductVariant>(
      `${JIVANDHARA_BASE}/product-variants/`,
      {
        page: 1,
        page_size: 100,
        search: search || undefined,
        status: "active",
      }
    );
    return result.data.map(item => {
      const labelValue = item.value ?? item.weight ?? "";
      const skuPart = item.sku ? ` (${item.sku})` : "";
      return {
        value: String(item.id),
        label: `${item.product_name ?? `Product #${item.product}`} - ${labelValue}${skuPart}`,
      };
    });
  };

  /** Lookup for variant masters (option titles) used on the Add Product form. */
  listMasterLookup = async (search = "") => {
    const result = await this.listPage({
      page: 1,
      page_size: 100,
      search: search || undefined,
      status: "active",
    });
    return result.data.map(item => ({
      value: String(item.id),
      label: item.title,
    }));
  };

  adjustStock = async (
    variantId: number,
    payload: { stock_action: "ADD" | "REMOVE"; stock_quantity: number }
  ): Promise<any> => {
    return await patch<any, typeof payload>(
      `${JIVANDHARA_BASE}/product-variants/${variantId}/`,
      payload,
      SILENT
    );
  };
}

export const gruhUdhyogVariantService = new GruhUdhyogVariantService();
